/**
 * Incident Management
 * ===================
 * Issue escalation, incident response, and resolution protocols.
 * 
 * Route: /wyldecrest/helpdesk/incident-management
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  AlertTriangle, 
  Phone, 
  Shield, 
  Clock,
  Users,
  FileText
} from 'lucide-react';

const sections = [
  { id: 'incident-classification', title: 'Incident Classification' },
  { id: 'response-procedures', title: 'Response Procedures' },
  { id: 'escalation-matrix', title: 'Escalation Matrix' },
  { id: 'communication-protocol', title: 'Communication Protocol' },
  { id: 'post-incident', title: 'Post-Incident Review' },
];

export default function IncidentManagementPage() {
  return (
    <GovernancePageLayout
      title="Incident Management"
      subtitle="Issue escalation, incident response, and resolution protocols"
      icon={AlertTriangle}
      version="1.3"
      lastReviewed="2026-05-19"
      reviewedBy="Operations Director"
      sections={sections}
    >
      {/* Incident Classification */}
      <DocumentSection title="Incident Classification" icon={Shield} id="incident-classification">
        <p className="text-slate-300 mb-6">
          All incidents are classified by severity to determine appropriate response.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Severity Levels</h3>
        
        <div className="space-y-4">
          <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded">SEV 1</span>
              <h4 className="text-red-300 font-semibold">Critical</h4>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              Major impact on operations or safety. Immediate response required.
            </p>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Gas leak or explosion risk</li>
              <li>• Major water damage (multiple properties)</li>
              <li>• Fire or security breach</li>
              <li>• System-wide outage</li>
              <li>• Serious injury on site</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-red-800/50 text-xs">
              <span className="text-red-400 font-medium">Response: Immediate</span>
              <span className="text-slate-500 mx-2">|</span>
              <span className="text-slate-400">Notify: Director + Client</span>
            </div>
          </div>
          
          <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-amber-600 text-white text-xs font-bold rounded">SEV 2</span>
              <h4 className="text-amber-300 font-semibold">High</h4>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              Significant impact on service delivery or customer experience.
            </p>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Contractor no-show on emergency</li>
              <li>• Multiple SLA breaches</li>
              <li>• Quality failure requiring re-work</li>
              <li>• Client escalation received</li>
              <li>• Compliance breach</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-amber-800/50 text-xs">
              <span className="text-amber-400 font-medium">Response: 1 hour</span>
              <span className="text-slate-500 mx-2">|</span>
              <span className="text-slate-400">Notify: Ops Manager</span>
            </div>
          </div>
          
          <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded">SEV 3</span>
              <h4 className="text-blue-300 font-semibold">Medium</h4>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              Moderate impact requiring attention but not immediate action.
            </p>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Single SLA breach</li>
              <li>• Customer complaint</li>
              <li>• Contractor dispute</li>
              <li>• Documentation gaps</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-blue-800/50 text-xs">
              <span className="text-blue-400 font-medium">Response: 4 hours</span>
              <span className="text-slate-500 mx-2">|</span>
              <span className="text-slate-400">Notify: Team Lead</span>
            </div>
          </div>
          
          <div className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-slate-600 text-white text-xs font-bold rounded">SEV 4</span>
              <h4 className="text-slate-300 font-semibold">Low</h4>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              Minor issues that can be addressed in normal course.
            </p>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Process improvement suggestions</li>
              <li>• Minor system issues</li>
              <li>• Training needs identified</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-slate-700/50 text-xs">
              <span className="text-slate-400 font-medium">Response: 24 hours</span>
              <span className="text-slate-500 mx-2">|</span>
              <span className="text-slate-400">Notify: Helpdesk Manager</span>
            </div>
          </div>
        </div>
      </DocumentSection>

      {/* Response Procedures */}
      <DocumentSection title="Response Procedures" icon={Clock} id="response-procedures">
        <p className="text-slate-300 mb-6">
          Structured response procedures ensure consistent incident handling.
        </p>

        <SOPStep 
          number={1}
          title="Acknowledge"
          description="Log and acknowledge the incident immediately."
          substeps={[
            'Create incident record with all known details',
            'Assign initial severity level',
            'Notify affected parties of receipt',
            'Assign incident owner'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Assess"
          description="Gather information and assess impact."
          substeps={[
            'Determine scope of impact',
            'Identify affected customers/properties',
            'Validate or adjust severity level',
            'Identify resources needed'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Contain"
          description="Take immediate action to prevent escalation."
          substeps={[
            'Implement temporary fixes if needed',
            'Communicate status to stakeholders',
            'Engage additional resources as required',
            'Document all actions taken'
          ]}
        />

        <SOPStep 
          number={4}
          title="Resolve"
          description="Implement permanent resolution."
          substeps={[
            'Identify root cause',
            'Implement fix or workaround',
            'Verify resolution is effective',
            'Update all affected parties'
          ]}
        />

        <SOPStep 
          number={5}
          title="Close"
          description="Complete incident documentation and close."
          substeps={[
            'Document final resolution',
            'Confirm with affected parties',
            'Schedule post-incident review (SEV 1-2)',
            'Close incident record'
          ]}
        />
      </DocumentSection>

      {/* Escalation Matrix */}
      <DocumentSection title="Escalation Matrix" icon={Users} id="escalation-matrix">
        <p className="text-slate-300 mb-6">
          Clear escalation paths ensure issues reach the right people at the right time.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Primary Escalation Path</h3>
        <EscalationPath levels={['Helpdesk Agent', 'Team Lead', 'Ops Manager', 'Director', 'Executive']} />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Escalation Triggers</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Condition</th>
                <th className="pb-2">Escalate To</th>
                <th className="pb-2">Timeframe</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">SEV 1 incident raised</td>
                <td>Director + On-call</td>
                <td>Immediate</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">SEV 2 not contained in 1 hour</td>
                <td>Director</td>
                <td>Automatic</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Client requests escalation</td>
                <td>Ops Manager</td>
                <td>Within 30 mins</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">No progress in 4 hours</td>
                <td>Next level</td>
                <td>Automatic</td>
              </tr>
              <tr>
                <td className="py-2">Resource conflict</td>
                <td>Ops Manager</td>
                <td>As identified</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Emergency Contacts</h3>
        
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
          <p className="text-red-300 font-medium mb-3">For SEV 1 incidents after hours:</p>
          <ul className="text-sm text-slate-300 space-y-2">
            <li>• <strong>On-Call Manager:</strong> Available via emergency hotline</li>
            <li>• <strong>Director:</strong> Mobile available for critical escalations</li>
            <li>• <strong>Client Emergency:</strong> Direct to on-call within 15 mins</li>
          </ul>
        </div>
      </DocumentSection>

      {/* Communication Protocol */}
      <DocumentSection title="Communication Protocol" icon={Phone} id="communication-protocol">
        <p className="text-slate-300 mb-6">
          Clear communication ensures all stakeholders remain informed throughout an incident.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Update Frequency</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Severity</th>
                <th className="pb-2">Internal Updates</th>
                <th className="pb-2">Client Updates</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 text-red-400">SEV 1</td>
                <td>Every 30 minutes</td>
                <td>Every 1 hour</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-amber-400">SEV 2</td>
                <td>Every 2 hours</td>
                <td>Every 4 hours</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-blue-400">SEV 3</td>
                <td>Twice daily</td>
                <td>Daily or on resolution</td>
              </tr>
              <tr>
                <td className="py-2 text-slate-400">SEV 4</td>
                <td>On resolution</td>
                <td>If relevant</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Checklist 
          title="Communication Checklist"
          items={[
            'Initial notification sent to all stakeholders',
            'Regular updates provided at scheduled intervals',
            'Client notified of any impact to their service',
            'Resolution confirmation sent',
            'Post-incident summary distributed',
            'All communications logged in incident record'
          ]}
        />
      </DocumentSection>

      {/* Post-Incident */}
      <DocumentSection title="Post-Incident Review" icon={FileText} id="post-incident">
        <p className="text-slate-300 mb-6">
          Post-incident reviews (PIR) are mandatory for SEV 1 and SEV 2 incidents.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">PIR Timeline</h3>
        
        <ul className="text-slate-300 space-y-2 mb-6">
          <li>• <strong className="text-white">SEV 1:</strong> PIR within 48 hours of resolution</li>
          <li>• <strong className="text-white">SEV 2:</strong> PIR within 5 working days</li>
          <li>• <strong className="text-white">SEV 3-4:</strong> PIR at discretion of Ops Manager</li>
        </ul>

        <h3 className="text-lg font-semibold text-white mb-4">PIR Template Sections</h3>
        
        <Checklist 
          title="PIR Documentation"
          items={[
            'Incident timeline (when detected, escalated, resolved)',
            'Root cause analysis',
            'Impact assessment (customers, cost, reputation)',
            'Response evaluation (what worked, what didn\'t)',
            'Action items with owners and due dates',
            'Lessons learned',
            'Sign-off from incident owner and reviewer'
          ]}
        />

        <WarningBox title="Action Tracking">
          All PIR action items must be entered into the action tracking system
          with assigned owners and due dates. Outstanding actions are reviewed weekly.
        </WarningBox>
      </DocumentSection>
    </GovernancePageLayout>
  );
}
